import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@airclou/ui';
import { replyToRequest, getErrorCode } from '@/services/api';
import { newIdempotencyKey } from '@/utils';

interface ReplyFormProps {
  ticketId: string;
  onReplySent: () => void;
}

export function ReplyForm({ ticketId, onReplySent }: ReplyFormProps) {
  const [message, setMessage] = useState('');
  const [state, setState] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // One key per reply, resent unchanged on a retry, so a reply whose answer
  // was lost is not stored twice. A new key once it is sent, and whenever the
  // text changes: an edited retry is a different reply, and reusing the key
  // would hand back the first one and drop the edit.
  const idempotencyKey = useRef<string | null>(null);
  useEffect(() => {
    idempotencyKey.current = null;
  }, [message]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setState('submitting');
    setErrorMsg(null);

    try {
      idempotencyKey.current ??= newIdempotencyKey();
      await replyToRequest(ticketId, { message: message.trim(), idempotencyKey: idempotencyKey.current });
      idempotencyKey.current = null;
      setMessage('');
      setState('idle');
      onReplySent();
    } catch (error) {
      setState('error');
      const code = getErrorCode(error);
      setErrorMsg(
        code === 'RATE_LIMITED'
          ? 'Too many replies. Please wait a moment and try again.'
          : 'Failed to send reply. Please try again.'
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Type your reply..."
        rows={4}
        className="w-full px-3 py-2 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none field-text"
        disabled={state === 'submitting'}
      />
      {state === 'error' && errorMsg && (
        <p className="text-sm text-destructive">{errorMsg}</p>
      )}
      <div className="flex justify-end">
        <Button type="submit" disabled={state === 'submitting' || !message.trim()}>
          {state === 'submitting' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Send reply
        </Button>
      </div>
    </form>
  );
}
